import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, CheckCircle2 } from "lucide-react";

export default function MyPolicies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/policies/mine");
        setPolicies(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const pending = policies.filter((p) => !p.signed_by_me && (p.open_slot_count || 0) > 0);
  const signed = policies.filter((p) => p.signed_by_me);

  return (
    <div>
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">My profile</div>
        <h1 className="text-4xl font-bold text-zinc-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Policies to sign</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Company policies attached to your Position Profile. Sign each one — the system stamps date, time and IP.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : (
        <>
          <Section title="Awaiting your signature" count={pending.length}>
            {pending.length === 0 ? (
              <Empty>Nothing pending — you&apos;re fully signed off.</Empty>
            ) : pending.map((p) => (
              <PolicyRow key={p.id} p={p} action={
                <Link
                  to={`/policies/${p.id}`}
                  data-testid={`sign-policy-${p.id}`}
                  style={{ background: "var(--brand-primary)" }}
                  className="text-white px-3 py-1.5 rounded-sm text-xs font-medium hover:opacity-90"
                >
                  Review &amp; sign
                </Link>
              } />
            ))}
          </Section>

          <Section title="Already signed" count={signed.length}>
            {signed.length === 0 ? (
              <Empty>No signed policies yet.</Empty>
            ) : signed.map((p) => (
              <PolicyRow key={p.id} p={p} action={
                <Link to={`/policies/${p.id}`} className="text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-300 px-3 py-1.5 rounded-sm hover:bg-zinc-50">View</Link>
              } signed />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

function Section({ title, count, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-zinc-500" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">{title}</h2>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-white text-zinc-700 border-zinc-200">{count}</span>
      </div>
      <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">{children}</div>
    </div>
  );
}

function Empty({ children }) { return <div className="px-5 py-8 text-center text-zinc-400 text-sm">{children}</div>; }

function PolicyRow({ p, action, signed }) {
  return (
    <div className="px-5 py-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm flex items-center gap-2">
          {signed && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
          {p.name}
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          Uploaded {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"} · {p.open_slot_count || 0} open slot{(p.open_slot_count || 0) === 1 ? "" : "s"}
        </div>
      </div>
      {action}
    </div>
  );
}
